import React from "react";
import ServicePageLayout, { H2, H3, P, UL } from "./ServicePageLayout";

export const meta = {
  title: "Commercial Security Cameras Alberta | UniFi Protect Installation | Epicom Systems",
  metaDescription:
    "Commercial IP camera installation across Southern Alberta — UniFi Protect, PoE cabling, local NVR recording, remote viewing and camera systems built as part of the same network we install everything else on.",
  canonicalPath: "/security-cameras",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/security-cameras#service",
    "name": "Commercial Security Camera Installation",
    "serviceType": "IP camera and video surveillance installation",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "IP camera systems for businesses, farms, industrial sites and campgrounds across Southern Alberta — designed around UniFi Protect where appropriate, powered over Ethernet, recorded locally, viewable remotely and integrated with the rest of the site network.",
    "url": "https://epicom.ca/security-cameras",
  },
};

export default function SecurityCameras() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="Security Cameras"
      h1="IP Camera Systems Built on Real Network Infrastructure"
      intro="Epicom designs and installs commercial IP camera systems across Southern Alberta — for businesses, industrial sites, storage yards, campgrounds, acreages and multi-building operations. Most of our camera systems are built on Ubiquiti UniFi Protect and live on the same UniFi network we install for the rest of the site: one interface, one login, no per-camera cloud fees."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/access-control", label: "Access Control" },
        { to: "/unifi-installation", label: "UniFi Installation" },
        { to: "/commercial-networking", label: "Commercial Networking" },
      ]}
    >
      <H2>What we install</H2>
      <UL>
        <><strong className="text-white/90">UniFi Protect cameras</strong> — G4/G5 series and equivalents, sized for the actual field of view rather than by marketing megapixel count.</>
        <><strong className="text-white/90">PoE cabling and switching</strong> — every camera gets a proper Cat6 run to a PoE switch, terminated to spec, tested. No power-over-nothing surprises.</>
        <><strong className="text-white/90">Local NVR / Protect controller</strong> — recording lives on the site, on equipment you own. No forced cloud subscription; remote viewing works, but the video doesn’t depend on somebody else’s service to exist.</>
        <><strong className="text-white/90">Remote viewing</strong> — access from a phone or laptop over a properly-configured connection. No exposed cameras on the open internet.</>
        <><strong className="text-white/90">Camera-to-network integration</strong> — cameras on their own VLAN, isolated from staff and guest traffic, with only the traffic they actually need to move.</>
      </UL>

      <H2>Where cameras really need to go</H2>
      <P>
        Coverage is a placement problem, not a camera-count problem. A handful of cameras
        positioned where the actual activity happens (entrances, choke points, cash points,
        yard gates, high-value equipment, service counters, blind corners) is worth more
        than a wall of cameras that all look at empty parking lot. We plan placement based
        on how the site is used — the person who runs the business knows where trouble
        actually starts, and that conversation drives the layout.
      </P>

      <H2>Where a proper camera system pays off</H2>
      <UL>
        <>Commercial and retail spaces — entrances, back-of-house, cash points, storage rooms.</>
        <>Industrial and manufacturing facilities — shop floors, yards, receiving bays, fuel islands.</>
        <>Farms and acreages — shops, grain bins, feed yards, main gates, and outbuildings.</>
        <>Campgrounds and outdoor operations — entrance gates, service buildings, common areas.</>
        <>Multi-building sites where cameras at remote buildings feed back via <a href="/point-to-point-wireless" className="text-signal hover:underline">wireless links</a> instead of trenching cable.</>
        <>Home installs where a residential customer wants a real business-grade system rather than another consumer app-and-camera bundle.</>
      </UL>

      <H2>Why we build cameras on top of the network</H2>
      <H3>One environment</H3>
      <P>
        When cameras live on the same UniFi platform as the switching, the Wi-Fi and — if
        you have it — <a href="/access-control" className="text-signal hover:underline">Access</a>,
        it’s all one interface. Add a camera, adjust a VLAN, review a clip, share a link
        with a manager — same place, same login. That matters when something happens at 2 am
        and you need to be looking at the right footage in under a minute.
      </P>
      <H3>PoE done properly</H3>
      <P>
        Every camera is powered by the PoE switch, not by a wall wart in a wet spot. That
        means fewer failure points, cleaner installs, and the option to move a camera to a
        different location later without an electrician.
      </P>
      <H3>Local recording, honest cloud</H3>
      <P>
        Footage is stored on the local Protect controller — the customer owns their video.
        Remote viewing works through Ubiquiti’s remote access, which is a capability of the
        product, but you’re not renting per-camera storage from somebody else every month.
      </P>
      <H3>Network segmentation</H3>
      <P>
        Cameras are noisy and they should not share a VLAN with staff laptops or a point-of-sale
        network. We put them on their own segment, allow only the traffic they legitimately
        need, and keep the rest of the network out of their way.
      </P>

      <H2>Site-to-camera links over wireless</H2>
      <P>
        For remote cameras — gates, yards, outbuildings, secondary sites — we regularly
        feed them back to the main NVR over a{" "}
        <a href="/point-to-point-wireless" className="text-signal hover:underline">point-to-point wireless link</a>,
        which is often faster, cheaper and more reliable than trenching cable across a yard
        or under a road. That’s a pattern we regularly deploy.
      </P>

      <H2>Working with Epicom on a camera project</H2>
      <P>
        We start with a walk-through or a plan review, understand what the customer actually
        needs to see (and equally, what they don’t need footage of), scope the cameras and
        the cabling accordingly, and quote it honestly. The result is a system the owner can
        run day-to-day without having to call somebody every time they need to look at
        something.
      </P>
    </ServicePageLayout>
  );
}
